import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Política de Privacidad | Graphyra</title>
        <meta name="description" content="Política de Privacidad y protección de datos de Graphyra" />
      </Helmet>

      <div className="legal-container">
        <div className="legal-header">
          <h1 className="legal-title">Política de Privacidad</h1>
          <p className="legal-update">Última actualización: 3 de enero de 2026</p>
          <p className="legal-intro">
            Esta Política de Privacidad describe cómo Graphyra ("nosotros", "nuestro") recopila, usa, 
            comparte y protege tu información personal cuando utilizas nuestra Plataforma.
          </p>
          <div className="legal-notice">
            <strong>Tu privacidad es importante:</strong> Nos comprometemos a proteger tus datos personales 
            y respetar tu privacidad de acuerdo con las leyes aplicables de protección de datos.
          </div>
        </div>

        <nav className="legal-nav">
          <a href="#responsable">1. Responsable</a>
          <a href="#alcance">2. Alcance</a>
          <a href="#datos">3. Datos que Recopilamos</a>
          <a href="#obtencion">4. Cómo Obtenemos Datos</a>
          <a href="#finalidades">5. Finalidades</a>
          <a href="#bases">6. Bases Legales</a>
          <a href="#comparticion">7. Compartición</a>
          <a href="#transferencias">8. Transferencias</a>
          <a href="#retencion">9. Retención</a>
          <a href="#seguridad">10. Seguridad</a>
          <a href="#derechos">11. Tus Derechos</a>
          <a href="#menores">12. Menores</a>
          <a href="#decisiones">13. Decisiones Automatizadas</a>
          <a href="#cambios">14. Cambios</a>
          <a href="#contacto">15. Contacto</a>
        </nav>

        <div className="legal-content">
          <section id="responsable" className="legal-section">
            <h2>1. Identificación del Responsable</h2>
            <div className="info-box">
              <p><strong>Responsable del tratamiento:</strong> Luis Cepeda ("Graphyra")</p>
              <p><strong>Teléfono:</strong> +57 3115491733</p>
              <p><strong>Correo de privacidad:</strong> admin.graphyra@gmail.com</p>
              <p><strong>Correo de contacto:</strong> contact.graphyra@gmail.com</p>
              <p><strong>Sitio web:</strong> www.graphyra.com</p>
            </div>
          </section>

          <section id="alcance" className="legal-section">
            <h2>2. Alcance de Esta Política</h2>
            <p>
              Esta Política de Privacidad se aplica a toda la información personal recopilada a través de:
            </p>
            <ul className="legal-list">
              <li>Nuestro sitio web (www.graphyra.com)</li>
              <li>Servicios y funcionalidades de la Plataforma</li>
              <li>Aplicaciones móviles relacionadas (si las hay)</li>
              <li>Comunicaciones por correo electrónico</li>
              <li>Interacciones en redes sociales oficiales</li>
            </ul>
            <p className="legal-note">
              Esta Política no se aplica a sitios web, servicios o aplicaciones de terceros, incluso si están 
              enlazados desde nuestra Plataforma. Te recomendamos revisar las políticas de privacidad de esos terceros.
            </p>
          </section>

          <section id="datos" className="legal-section">
            <h2>3. Datos Personales que Recopilamos</h2>
            
            <h3>3.1. Información de Cuenta y Perfil</h3>
            <div className="info-box">
              <h4>Datos obligatorios para crear una cuenta:</h4>
              <ul className="legal-list">
                <li><strong>Nombre de usuario:</strong> Identificador único para tu cuenta</li>
                <li><strong>Nombre completo:</strong> Tu nombre real</li>
                <li><strong>Correo electrónico:</strong> Para comunicaciones y recuperación de cuenta</li>
                <li><strong>Contraseña:</strong> Almacenada de forma cifrada (hash)</li>
              </ul>
            </div>

            <div className="info-box">
              <h4>Datos opcionales de perfil:</h4>
              <ul className="legal-list">
                <li><strong>Número de teléfono:</strong> Para notificaciones y verificación</li>
                <li><strong>Institución u organización:</strong> Contexto académico/profesional</li>
                <li><strong>Ocupación:</strong> Información profesional</li>
                <li><strong>Avatar/foto de perfil:</strong> Imagen representativa (puede ser prediseñada o subida por ti)</li>
                <li><strong>Biografía:</strong> Descripción personal</li>
              </ul>
            </div>

            <h3>3.2. Contenido Generado por el Usuario</h3>
            <ul className="legal-list">
              <li><strong>Publicaciones:</strong> Artículos, memes, posts y otro contenido que compartas</li>
              <li><strong>Comentarios:</strong> Respuestas y participación en discusiones</li>
              <li><strong>Reacciones:</strong> Likes, favoritos y otras interacciones</li>
              <li><strong>Archivos multimedia:</strong> Imágenes, videos y otros medios que subas</li>
              <li><strong>Mensajes:</strong> Comunicaciones dentro de la plataforma (si están disponibles)</li>
            </ul>

            <h3>3.3. Datos Técnicos y de Uso</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Información de dispositivo y navegador</strong>
                <p>
                  Tipo de dispositivo, sistema operativo, navegador, versión del software, 
                  resolución de pantalla, zona horaria, plugins instalados.
                </p>
              </div>
              <div className="definition-item">
                <strong>Datos de conexión</strong>
                <p>
                  Dirección IP (puede ser aproximada o anonimizada), proveedor de servicios de Internet (ISP), 
                  ubicación geográfica aproximada basada en IP.
                </p>
              </div>
              <div className="definition-item">
                <strong>Datos de navegación y uso</strong>
                <p>
                  Páginas visitadas, URLs de referencia, tiempo de permanencia, clics, interacciones, 
                  funcionalidades utilizadas, contenido visualizado.
                </p>
              </div>
              <div className="definition-item">
                <strong>Identificadores y cookies</strong>
                <p>
                  IDs de sesión, tokens de autenticación, identificadores de dispositivo, cookies 
                  (ver nuestra <Link to="/cookies">Política de Cookies</Link> para más detalles).
                </p>
              </div>
            </div>

            <h3>3.4. Datos de Seguridad y Prevención de Fraude</h3>
            <ul className="legal-list">
              <li><strong>Registros de acceso (logs):</strong> Fecha, hora y detalles de cada acceso a tu cuenta</li>
              <li><strong>Historial de actividad sospechosa:</strong> Intentos fallidos de login, accesos anómalos</li>
              <li><strong>Datos de verificación:</strong> Resultados de verificaciones anti-bot (como reCAPTCHA)</li>
              <li><strong>Información de seguridad:</strong> Configuraciones de seguridad de tu cuenta</li>
            </ul>

            <h3>3.5. Datos de Comunicación</h3>
            <ul className="legal-list">
              <li>Contenido de mensajes que nos envíes (soporte, consultas, reportes)</li>
              <li>Historial de comunicaciones con nuestro equipo de soporte</li>
              <li>Feedback y sugerencias que compartas voluntariamente</li>
            </ul>
          </section>

          <section id="obtencion" className="legal-section">
            <h2>4. Cómo Obtenemos Tus Datos</h2>
            
            <h3>4.1. Información que Tú Proporcionas Directamente</h3>
            <ul className="legal-list">
              <li>Al crear o actualizar tu cuenta</li>
              <li>Al publicar contenido, comentar o reaccionar</li>
              <li>Al configurar preferencias y ajustes</li>
              <li>Al contactar con soporte o enviarnos mensajes</li>
              <li>Al participar en encuestas o formularios</li>
            </ul>

            <h3>4.2. Información Recopilada Automáticamente</h3>
            <ul className="legal-list">
              <li><strong>Durante el uso del servicio:</strong> Datos técnicos, de navegación y uso</li>
              <li><strong>Mediante cookies:</strong> Ver <Link to="/cookies">Política de Cookies</Link></li>
              <li><strong>Logs del servidor:</strong> Registros automáticos de accesos y eventos</li>
              <li><strong>Analítica:</strong> Métricas sobre rendimiento y uso de funcionalidades</li>
            </ul>

            <h3>4.3. Información de Terceros</h3>
            <p>
              En algunos casos, podemos recibir información de proveedores de servicios de seguridad 
              (como verificación anti-bot), pero no compartimos ni vendemos tu información a terceros 
              con fines de marketing.
            </p>
          </section>

          <section id="finalidades" className="legal-section">
            <h2>5. Finalidades del Tratamiento de Datos</h2>
            
            <h3>5.1. Provisión y Operación del Servicio</h3>
            <div className="info-box">
              <p>Utilizamos tus datos para:</p>
              <ul className="legal-list">
                <li>Crear y administrar tu cuenta de usuario</li>
                <li>Permitir el acceso a funcionalidades autenticadas</li>
                <li>Mostrar tu perfil y contenido a otros usuarios</li>
                <li>Procesar y publicar tus publicaciones, comentarios y reacciones</li>
                <li>Generar feeds personalizados y recomendaciones de contenido</li>
                <li>Gestionar interacciones sociales (follows, likes, compartir)</li>
              </ul>
            </div>

            <h3>5.2. Comunicación Contigo</h3>
            <ul className="legal-list">
              <li>Enviar notificaciones sobre actividad en tu cuenta</li>
              <li>Responder a tus consultas y solicitudes de soporte</li>
              <li>Informarte sobre cambios en términos, políticas o servicios</li>
              <li>Enviar actualizaciones importantes sobre la plataforma</li>
              <li>Solicitar feedback o participación en encuestas (con tu consentimiento)</li>
            </ul>

            <h3>5.3. Seguridad y Prevención de Abuso</h3>
            <div className="warning-box">
              <p><strong>Protección esencial:</strong></p>
              <ul className="legal-list">
                <li>Detectar y prevenir fraude, spam y actividades maliciosas</li>
                <li>Verificar identidad y autenticidad de usuarios (prevenir bots)</li>
                <li>Proteger contra accesos no autorizados y ataques cibernéticos</li>
                <li>Investigar violaciones de nuestros Términos y Condiciones</li>
                <li>Cumplir con obligaciones legales y responder a requerimientos legales</li>
              </ul>
            </div>

            <h3>5.4. Mejora y Desarrollo del Servicio</h3>
            <ul className="legal-list">
              <li>Analizar patrones de uso para mejorar funcionalidades</li>
              <li>Diagnosticar problemas técnicos y optimizar rendimiento</li>
              <li>Realizar pruebas A/B y experimentación de nuevas características</li>
              <li>Desarrollar nuevos productos y servicios</li>
              <li>Generar estadísticas y analítica interna (datos agregados y anonimizados)</li>
            </ul>

            <h3>5.5. Personalización de Experiencia</h3>
            <ul className="legal-list">
              <li>Adaptar contenido a tus intereses y preferencias</li>
              <li>Recordar tus configuraciones y preferencias</li>
              <li>Sugerir contenido, usuarios o comunidades relevantes</li>
              <li>Personalizar la interfaz según tu uso</li>
            </ul>
          </section>

          <section id="bases" className="legal-section">
            <h2>6. Bases Legales del Tratamiento</h2>
            <p>
              Dependiendo de tu jurisdicción (especialmente si te encuentras en el Espacio Económico Europeo, 
              Reino Unido o regiones con leyes similares), procesamos tus datos personales basándonos en:
            </p>

            <div className="definition-list">
              <div className="definition-item">
                <strong>📝 Ejecución de un Contrato</strong>
                <p>
                  El tratamiento es necesario para cumplir con nuestro acuerdo contigo (Términos y Condiciones) 
                  y proporcionar los servicios que has solicitado al crear una cuenta.
                </p>
                <p><em>Aplica a:</em> Creación de cuenta, operación del servicio, funcionalidades principales.</p>
              </div>

              <div className="definition-item">
                <strong>✅ Consentimiento</strong>
                <p>
                  Has dado tu consentimiento explícito para el tratamiento de datos para propósitos específicos.
                </p>
                <p><em>Aplica a:</em> Cookies no esenciales, marketing (si aplicable), algunas analíticas opcionales.</p>
              </div>

              <div className="definition-item">
                <strong>⚖️ Interés Legítimo</strong>
                <p>
                  El tratamiento es necesario para nuestros intereses legítimos o los de terceros, siempre que 
                  no prevalezcan tus derechos e intereses fundamentales.
                </p>
                <p><em>Aplica a:</em> Seguridad, prevención de fraude, mejora del servicio, analítica interna.</p>
              </div>

              <div className="definition-item">
                <strong>⚖️ Obligación Legal</strong>
                <p>
                  El tratamiento es necesario para cumplir con obligaciones legales a las que estamos sujetos.
                </p>
                <p><em>Aplica a:</em> Cumplimiento de leyes, respuesta a autoridades, retención de datos requerida.</p>
              </div>
            </div>
          </section>

          <section id="comparticion" className="legal-section">
            <h2>7. Compartición de Datos con Terceros</h2>
            
            <div className="warning-box">
              <h4>🔒 Compromiso de privacidad:</h4>
              <p><strong>NO VENDEMOS tu información personal a terceros.</strong></p>
              <p>
                Solo compartimos tus datos en las circunstancias limitadas descritas a continuación, 
                siempre con las salvaguardias apropiadas.
              </p>
            </div>

            <h3>7.1. Proveedores de Servicios y Socios</h3>
            <p>
              Compartimos datos con proveedores de confianza que nos ayudan a operar la Plataforma:
            </p>
            <ul className="legal-list">
              <li><strong>Infraestructura y hosting:</strong> Proveedores de servidores, bases de datos, almacenamiento en la nube</li>
              <li><strong>Seguridad:</strong> Servicios anti-bot, anti-fraude y protección DDoS (ej: Google reCAPTCHA)</li>
              <li><strong>Comunicaciones:</strong> Servicios de email transaccional y notificaciones</li>
              <li><strong>Analítica:</strong> Herramientas de análisis de uso y rendimiento (si aplican)</li>
              <li><strong>Monitoreo:</strong> Servicios de monitoreo de errores y rendimiento (si aplican)</li>
            </ul>
            <p className="legal-note">
              Estos proveedores tienen acceso limitado a tus datos solo en la medida necesaria para realizar 
              servicios en nuestro nombre y están obligados contractualmente a proteger tu información.
            </p>

            <h3>7.2. Compartición por Obligación Legal</h3>
            <p>Podemos divulgar información personal si:</p>
            <ul className="legal-list">
              <li>Lo exige una ley, regulación, proceso legal o solicitud gubernamental</li>
              <li>Es necesario para hacer cumplir nuestros términos o políticas</li>
              <li>Es necesario para proteger derechos, propiedad o seguridad de Graphyra, usuarios o el público</li>
              <li>Lo requiere una autoridad competente en una investigación legal</li>
            </ul>

            <h3>7.3. Transferencias Corporativas</h3>
            <p>
              En caso de fusión, adquisición, venta de activos o reestructuración, tus datos pueden ser 
              transferidos a la entidad sucesora, quien estará obligada a continuar protegiendo tu información 
              según esta política.
            </p>

            <h3>7.4. Información Públicamente Visible</h3>
            <p>
              Cierta información de tu perfil y tu contenido público serán visibles para otros usuarios 
              y potencialmente para el público en general:
            </p>
            <ul className="legal-list">
              <li>Nombre de usuario y nombre de perfil</li>
              <li>Avatar/foto de perfil</li>
              <li>Biografía y información de perfil que elijas hacer pública</li>
              <li>Publicaciones, comentarios y reacciones públicas</li>
              <li>Listas de follows/seguidores (según tu configuración de privacidad)</li>
            </ul>
            <p className="legal-note">
              Puedes controlar la visibilidad de cierta información mediante tus configuraciones de privacidad.
            </p>
          </section>

          <section id="transferencias" className="legal-section">
            <h2>8. Transferencias Internacionales de Datos</h2>
            <p>
              La operación de servicios en línea puede implicar que tus datos se transfieran y procesen en 
              países distintos al tuyo, incluyendo países fuera del Espacio Económico Europeo (EEE) que pueden 
              no ofrecer el mismo nivel de protección de datos.
            </p>

            <h3>8.1. Salvaguardias de Transferencia</h3>
            <p>Cuando transferimos datos internacionalmente, implementamos salvaguardias apropiadas:</p>
            <ul className="legal-list">
              <li><strong>Cláusulas Contractuales Estándar (SCC):</strong> Contratos aprobados por autoridades de protección de datos</li>
              <li><strong>Decisiones de Adecuación:</strong> Transferencias a países reconocidos con protección adecuada</li>
              <li><strong>Certificaciones de privacidad:</strong> Proveedores certificados bajo marcos de privacidad reconocidos</li>
              <li><strong>Medidas técnicas:</strong> Encriptación y pseudonimización de datos transferidos</li>
            </ul>

            <h3>8.2. Proveedores Internacionales</h3>
            <p>
              Algunos de nuestros proveedores de servicios pueden estar ubicados en Estados Unidos u otros países. 
              Evaluamos cuidadosamente la protección de datos que ofrecen antes de trabajar con ellos.
            </p>
          </section>

          <section id="retencion" className="legal-section">
            <h2>9. Retención de Datos Personales</h2>
            
            <h3>9.1. Principios de Retención</h3>
            <p>
              Solo conservamos tus datos personales durante el tiempo necesario para cumplir con los propósitos 
              para los que fueron recopilados y según lo requieran las obligaciones legales.
            </p>

            <h3>9.2. Períodos de Retención</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Cuenta Activa</strong>
                <p>
                  Mientras tu cuenta permanezca activa, conservamos tu información de perfil y contenido 
                  para proporcionar el servicio.
                </p>
              </div>
              <div className="definition-item">
                <strong>Cuenta Cerrada</strong>
                <p>
                  Tras el cierre de cuenta, eliminamos o anonimizamos la mayoría de tus datos personales 
                  dentro de [30-90 días], excepto lo que debamos retener por obligaciones legales.
                </p>
              </div>
              <div className="definition-item">
                <strong>Datos de Seguridad y Logs</strong>
                <p>
                  Logs de acceso y seguridad se retienen típicamente por [6-12 meses] para investigación 
                  de incidentes y cumplimiento legal.
                </p>
              </div>
              <div className="definition-item">
                <strong>Obligaciones Legales</strong>
                <p>
                  Ciertos datos pueden retenerse por períodos más largos cuando lo exija la ley 
                  (por ejemplo, registros fiscales, contables o ante requerimientos legales).
                </p>
              </div>
            </div>

            <h3>9.3. Criterios de Retención</h3>
            <p>Determinamos períodos de retención considerando:</p>
            <ul className="legal-list">
              <li>Duración de tu relación con la Plataforma</li>
              <li>Obligaciones legales y regulatorias aplicables</li>
              <li>Necesidades de resolución de disputas o cumplimiento de términos</li>
              <li>Necesidades de seguridad, prevención de fraude y auditoría</li>
              <li>Consentimiento que nos hayas otorgado</li>
            </ul>
          </section>

          <section id="seguridad" className="legal-section">
            <h2>10. Seguridad de la Información</h2>
            
            <h3>10.1. Compromiso con la Seguridad</h3>
            <p>
              Implementamos medidas técnicas y organizativas razonables y apropiadas para proteger tus datos 
              personales contra acceso no autorizado, alteración, divulgación o destrucción.
            </p>

            <h3>10.2. Medidas de Seguridad Técnicas</h3>
            <div className="info-box">
              <h4>🔐 Protecciones implementadas:</h4>
              <ul className="legal-list">
                <li><strong>Encriptación:</strong> Contraseñas hasheadas (bcrypt), comunicaciones HTTPS, datos sensibles encriptados</li>
                <li><strong>Control de acceso:</strong> Autenticación JWT, limitación de privilegios, gestión de sesiones</li>
                <li><strong>Protección de red:</strong> Firewalls, protección DDoS, monitoreo de intrusiones</li>
                <li><strong>Backups seguros:</strong> Copias de seguridad regulares en ubicaciones seguras</li>
                <li><strong>Auditorías:</strong> Revisiones de seguridad regulares y pruebas de vulnerabilidades</li>
              </ul>
            </div>

            <h3>10.3. Medidas Organizativas</h3>
            <ul className="legal-list">
              <li>Acceso limitado a datos personales solo para personal autorizado</li>
              <li>Capacitación en seguridad y privacidad para el equipo</li>
              <li>Políticas y procedimientos de seguridad documentados</li>
              <li>Acuerdos de confidencialidad con empleados y proveedores</li>
            </ul>

            <h3>10.4. Limitaciones de Seguridad</h3>
            <div className="warning-box">
              <p>
                <strong>Importante:</strong> A pesar de nuestros esfuerzos, ningún sistema de seguridad es 
                100% infalible. No podemos garantizar seguridad absoluta. En caso de brecha de seguridad que 
                afecte tus datos, te notificaremos según lo requiera la ley aplicable.
              </p>
            </div>

            <h3>10.5. Tu Responsabilidad</h3>
            <p>También es importante que tú tomes medidas para proteger tu cuenta:</p>
            <ul className="legal-list">
              <li>Usa una contraseña fuerte y única</li>
              <li>No compartas tus credenciales con nadie</li>
              <li>Cierra sesión en dispositivos compartidos</li>
              <li>Habilita verificación en dos pasos si está disponible</li>
              <li>Reporta actividad sospechosa inmediatamente</li>
            </ul>
          </section>

          <section id="derechos" className="legal-section">
            <h2>11. Tus Derechos de Privacidad</h2>
            <p>
              Dependiendo de tu ubicación geográfica y las leyes aplicables, puedes tener ciertos derechos 
              respecto a tus datos personales.
            </p>

            <h3>11.1. Derechos Principales (GDPR y leyes similares)</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>👁️ Derecho de Acceso</strong>
                <p>
                  Puedes solicitar una copia de los datos personales que tenemos sobre ti, incluyendo 
                  información sobre cómo los procesamos.
                </p>
              </div>

              <div className="definition-item">
                <strong>✏️ Derecho de Rectificación</strong>
                <p>
                  Puedes solicitar la corrección de datos personales inexactos o la completación de datos incompletos.
                </p>
              </div>

              <div className="definition-item">
                <strong>🗑️ Derecho de Supresión ("Derecho al Olvido")</strong>
                <p>
                  Puedes solicitar la eliminación de tus datos personales en ciertas circunstancias, 
                  sujeto a excepciones legales.
                </p>
              </div>

              <div className="definition-item">
                <strong>⏸️ Derecho de Limitación del Tratamiento</strong>
                <p>
                  Puedes solicitar que restrinjamos el procesamiento de tus datos en determinadas situaciones.
                </p>
              </div>

              <div className="definition-item">
                <strong>📦 Derecho de Portabilidad</strong>
                <p>
                  Puedes solicitar recibir tus datos en un formato estructurado, de uso común y lectura mecánica, 
                  y transmitirlos a otro responsable.
                </p>
              </div>

              <div className="definition-item">
                <strong>🚫 Derecho de Oposición</strong>
                <p>
                  Puedes oponerte al procesamiento de tus datos basado en intereses legítimos o para 
                  fines de marketing directo.
                </p>
              </div>

              <div className="definition-item">
                <strong>🤖 Derecho a No Ser Objeto de Decisiones Automatizadas</strong>
                <p>
                  Puedes solicitar revisión humana de decisiones tomadas únicamente por medios automatizados 
                  que te afecten significativamente.
                </p>
              </div>

              <div className="definition-item">
                <strong>❌ Derecho a Retirar el Consentimiento</strong>
                <p>
                  Si el tratamiento se basa en tu consentimiento, puedes retirarlo en cualquier momento, 
                  sin afectar la licitud del tratamiento previo.
                </p>
              </div>
            </div>

            <h3>11.2. Cómo Ejercer Tus Derechos</h3>
            <p>Para ejercer cualquiera de estos derechos:</p>
            <ol className="legal-list" style={{ listStyle: 'decimal', paddingLeft: '2rem' }}>
              <li>Envía un correo a <strong>admin.graphyra@gmail.com</strong></li>
              <li>Incluye tu nombre completo, dirección de email asociada a tu cuenta</li>
              <li>Especifica claramente qué derecho deseas ejercer</li>
              <li>Proporciona información adicional si es necesaria para verificar tu identidad</li>
            </ol>
            <p className="legal-note">
              <strong>Plazo de respuesta:</strong> Responderemos a tu solicitud dentro de [30 días / 1 mes] 
              desde su recepción, pudiendo extender este plazo en casos complejos.
            </p>

            <h3>11.3. Verificación de Identidad</h3>
            <p>
              Para proteger tu privacidad, es posible que necesitemos verificar tu identidad antes de procesar 
              tu solicitud. Esto puede incluir solicitar información adicional o documentación.
            </p>

            <h3>11.4. Derecho a Presentar Reclamo</h3>
            <p>
              Si no estás satisfecho con nuestra respuesta, tienes derecho a presentar una queja ante la autoridad 
              de protección de datos de tu jurisdicción:
            </p>
            <ul className="legal-list">
              <li><strong>UE/EEE:</strong> Autoridad de protección de datos de tu país</li>
              <li><strong>España:</strong> Agencia Española de Protección de Datos (AEPD)</li>
              <li><strong>Reino Unido:</strong> Information Commissioner's Office (ICO)</li>
              <li><strong>Otros países:</strong> Autoridad competente local</li>
            </ul>
          </section>

          <section id="menores" className="legal-section">
            <h2>12. Menores de Edad</h2>
            
            <h3>12.1. Restricción de Edad</h3>
            <div className="warning-box">
              <p>
                <strong>Nuestra Plataforma no está dirigida a menores de 18 años</strong> (o la edad legal 
                de consentimiento en tu jurisdicción, si es mayor).
              </p>
              <p>
                No recopilamos intencionalmente información personal de menores sin el consentimiento verificable 
                de sus padres o tutores legales.
              </p>
            </div>

            <h3>12.2. Si Eres Menor de Edad</h3>
            <p>
              Si eres menor de 18 años (o la edad requerida en tu jurisdicción), no debes:
            </p>
            <ul className="legal-list">
              <li>Crear una cuenta en Graphyra</li>
              <li>Proporcionar información personal a través de la Plataforma</li>
              <li>Usar servicios que requieran verificación de edad</li>
            </ul>

            <h3>12.3. Si Eres Padre o Tutor</h3>
            <p>
              Si crees que un menor ha proporcionado información personal a Graphyra sin consentimiento apropiado:
            </p>
            <ul className="legal-list">
              <li>Contáctanos inmediatamente en <strong>admin.graphyra@gmail.com</strong></li>
              <li>Proporciona detalles del menor y evidencia de tu relación legal</li>
              <li>Tomaremos medidas para verificar y eliminar la información</li>
            </ul>
          </section>

          <section id="decisiones" className="legal-section">
            <h2>13. Decisiones Automatizadas y Elaboración de Perfiles</h2>
            
            <h3>13.1. Uso de Sistemas Automatizados</h3>
            <p>
              Utilizamos ciertos procesos automatizados para mejorar tu experiencia y proteger la plataforma:
            </p>
            <ul className="legal-list">
              <li><strong>Personalización de contenido:</strong> Algoritmos que sugieren contenido relevante</li>
              <li><strong>Detección de spam y abuso:</strong> Sistemas automáticos de moderación</li>
              <li><strong>Verificación de seguridad:</strong> Detección automática de bots y actividades sospechosas</li>
              <li><strong>Prevención de fraude:</strong> Análisis de patrones de comportamiento anómalo</li>
            </ul>

            <h3>13.2. Toma de Decisiones Significativas</h3>
            <p>
              <strong>No utilizamos decisiones exclusivamente automatizadas</strong> que produzcan efectos 
              jurídicos o te afecten significativamente de manera similar, sin intervención humana.
            </p>
            <p className="legal-note">
              Las medidas de seguridad automatizadas (como bloqueos temporales anti-spam) incluyen revisión 
              humana disponible a solicitud. Puedes contactar a soporte si crees que una acción automatizada 
              te afectó incorrectamente.
            </p>

            <h3>13.3. Tu Derecho a Intervención Humana</h3>
            <p>
              Si sientes que una decisión automatizada te ha afectado injustamente, puedes:
            </p>
            <ul className="legal-list">
              <li>Solicitar revisión humana de la decisión</li>
              <li>Expresar tu punto de vista sobre la decisión</li>
              <li>Impugnar la decisión</li>
            </ul>
          </section>

          <section id="cambios" className="legal-section">
            <h2>14. Cambios a Esta Política</h2>
            
            <h3>14.1. Actualizaciones de la Política</h3>
            <p>
              Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras 
              prácticas, tecnología, requisitos legales o por otras razones operativas.
            </p>

            <h3>14.2. Notificación de Cambios</h3>
            <p>Para cambios materiales, te notificaremos mediante:</p>
            <ul className="legal-list">
              <li>Aviso destacado en la Plataforma</li>
              <li>Correo electrónico a la dirección registrada en tu cuenta</li>
              <li>Notificación dentro de la aplicación</li>
            </ul>

            <h3>14.3. Fecha de Vigencia</h3>
            <p>
              La versión actualizada entrará en vigor en la fecha indicada como "Última actualización" al 
              inicio de este documento. El uso continuado de la Plataforma tras cambios constituye tu 
              aceptación de la Política revisada.
            </p>
            <p className="legal-note">
              <strong>Recomendación:</strong> Revisa esta Política periódicamente para mantenerte informado 
              sobre cómo protegemos tu información.
            </p>
          </section>

          <section id="contacto" className="legal-section">
            <h2>15. Contacto e Información Adicional</h2>
            
            <h3>15.1. Consultas sobre Privacidad</h3>
            <p>
              Para cualquier pregunta, comentario o solicitud relacionada con esta Política de Privacidad 
              o el tratamiento de tus datos personales:
            </p>
            <div className="contact-box">
              <p><strong>📧 Correo de privacidad:</strong> admin.graphyra@gmail.com</p>
              <p><strong>📧 Correo de contacto:</strong> contact.graphyra@gmail.com</p>
              <p><strong>📧 Correo de soporte:</strong> support.graphyra@gmail.com</p>
              <p><strong>📞 Teléfono:</strong> +57 3115491733</p>
              <p><strong>🌐 Formulario de contacto:</strong> <Link to="/contact">Página de contacto</Link></p>
            </div>

            <h3>15.2. Recursos Adicionales</h3>
            <div className="info-box">
              <p>Consulta también:</p>
              <ul className="legal-list">
                <li><Link to="/terms">Términos y Condiciones</Link> - Uso de la plataforma y normas</li>
                <li><Link to="/cookies">Política de Cookies</Link> - Información detallada sobre cookies</li>
                <li>[Configuración de privacidad] - Gestiona tus preferencias (si está disponible)</li>
              </ul>
            </div>

            <h3>15.3. Idioma de la Política</h3>
            <p>
              Esta Política está disponible en español. En caso de traducción a otros idiomas, 
              la versión en español prevalecerá en caso de conflicto o discrepancia.
            </p>
          </section>

          <div className="legal-footer">
            <p>
              Esta Política de Privacidad complementa nuestros Términos y Condiciones y forma parte de tu 
              acuerdo con Graphyra.
            </p>
            <div className="legal-links">
              <Link to="/terms">Términos y Condiciones</Link>
              <span className="separator">•</span>
              <Link to="/cookies">Política de Cookies</Link>
              <span className="separator">•</span>
              <Link to="/">Volver al inicio</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
